import validatePhoneNumber from "@faire/web-api/api/v2/addresses/validate-phone-number/post";
import { IValidatePhoneNumberResponse } from "@faire/web-api/indigofair/data/IValidatePhoneNumberResponse";
import { ISO3166Alpha3 } from "@faire/web-api/indigofair/iso3166/ISO3166Alpha3";
import { makeObservable } from "@faire/web/common/makeObservable";
import { computed, observable } from "mobx";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";

export class PhoneNumberValidator {
  constructor() {
    makeObservable(this);
  }

  private lastValidatingPhoneNumber?: string;

  @observable
  validatePhoneNumberPromise?: IPromiseBasedObservable<IValidatePhoneNumberResponse>;

  @computed
  get isPending(): boolean {
    return this.validatePhoneNumberPromise?.state === "pending";
  }

  @computed
  get isInvalid(): boolean {
    return (
      this.validatePhoneNumberPromise?.case({
        fulfilled: (res) => !res?.is_valid,
      }) ?? false
    );
  }

  /**
   * Checks whether a given phone number is valid. This is implemented by checking if the last phone number that we validated is
   * equal to the provided phone number. This is used to validate forms avoiding the scenario that occurs when the user changes
   * the phone number just before the previous phone number is validated, leading to the form potentially accepting an invalid
   * phone number.
   * @param phoneNumber
   */
  isPhoneNumberInvalid = (phoneNumber: string) => {
    return (
      this.lastValidatingPhoneNumber &&
      phoneNumber.localeCompare(this.lastValidatingPhoneNumber) === 0 &&
      this.isInvalid
    );
  };

  validatePhoneNumber = (phoneNumber: string, country?: string) => {
    if (phoneNumber.trim().length === 0) {
      this.validatePhoneNumberPromise = fromPromise(
        Promise.resolve({
          is_valid: false,
        })
      );
    } else {
      this.validatePhoneNumberPromise = fromPromise(
        validatePhoneNumber({
          phone_number: phoneNumber,
          country: country
            ? ISO3166Alpha3[country as keyof typeof ISO3166Alpha3]
            : undefined,
        })
      );
    }

    this.lastValidatingPhoneNumber = phoneNumber;
  };
}
